/**
 * A QR encoder — byte mode, error-correction level M, versions 1 to 6.
 *
 * The card points at a download page, and a printed-looking artifact that says "type this URL"
 * is asking for work a camera can do. Nothing in the app may reach the network to draw one
 * (CLAUDE.md: there is no backend, and a shared timetable has to work in a corridor with no
 * signal), and a QR image service is exactly the kind of runtime dependency this project does
 * not take — so the code is generated here, on the device, as a matrix of modules the card
 * paints as rectangles. Vector output also means it is crisp at any export scale, which a
 * fetched PNG would not be.
 *
 * Scope is deliberately the smallest thing that encodes a URL:
 *   - **Byte mode only.** Alphanumeric mode would pack an upper-cased URL tighter, but the
 *     saving is meaningless at this length and the extra mode is more to get wrong.
 *   - **Level M** (~15% recovery) — the level the format recommends for general use, and enough
 *     for a code read off a phone screen or a photo of one.
 *   - **Versions 1–6.** Version 7 and up carry an extra version-information block; stopping at 6
 *     keeps that whole branch out of the code, and still holds 106 bytes, which is far more than
 *     any link this app ships.
 *
 * Implements ISO/IEC 18004. Verified module-for-module against the `segno` reference encoder —
 * see `__tests__/qr.fixtures.ts` for the frozen matrices and how they were produced.
 */

/** `true` is a dark module. Indexed `[row][column]`, origin top-left. */
export type QrMatrix = boolean[][];

/* ------------------------------------------------------------------ *
 * GF(256) — the arithmetic Reed–Solomon is defined over
 * ------------------------------------------------------------------ */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  // The field generated by x^8 + x^4 + x^3 + x^2 + 1 (0x11d), as the format specifies.
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if ((x & 0x100) !== 0) x ^= 0x11d;
  }
  // Doubled so a product's exponent never needs a modulo.
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255] ?? 0;
}

const mul = (a: number, b: number): number =>
  a === 0 || b === 0 ? 0 : (EXP[(LOG[a] ?? 0) + (LOG[b] ?? 0)] ?? 0);

/** The generator polynomial ∏(x − α^i), highest-degree coefficient first. */
const generatorPoly = (degree: number): number[] => {
  let poly = [1];

  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] = (next[j] ?? 0) ^ (poly[j] ?? 0);
      next[j + 1] = (next[j + 1] ?? 0) ^ mul(poly[j] ?? 0, EXP[i] ?? 0);
    }
    poly = next;
  }

  return poly;
};

/** The error-correction codewords for one block: the remainder of data ÷ generator. */
const remainder = (data: readonly number[], degree: number): number[] => {
  const gen = generatorPoly(degree);
  const rem = new Array<number>(degree).fill(0);

  for (const byte of data) {
    const factor = byte ^ (rem.shift() ?? 0);
    rem.push(0);
    if (factor === 0) continue;
    for (let i = 0; i < degree; i += 1) rem[i] = (rem[i] ?? 0) ^ mul(gen[i + 1] ?? 0, factor);
  }

  return rem;
};

/* ------------------------------------------------------------------ *
 * Version tables (level M only)
 * ------------------------------------------------------------------ */

type VersionSpec = {
  /** Data codewords available to the payload, across every block. */
  data: number;
  /** Error-correction codewords per block. */
  ecPerBlock: number;
  blocks: number;
};

const VERSIONS: Record<number, VersionSpec> = {
  1: { data: 16, ecPerBlock: 10, blocks: 1 },
  2: { data: 28, ecPerBlock: 16, blocks: 1 },
  3: { data: 44, ecPerBlock: 26, blocks: 1 },
  4: { data: 64, ecPerBlock: 18, blocks: 2 },
  5: { data: 86, ecPerBlock: 24, blocks: 2 },
  6: { data: 108, ecPerBlock: 16, blocks: 4 },
};

const MAX_VERSION = 6;

const sizeOf = (version: number): number => version * 4 + 17;

/** Centre of the one alignment pattern versions 2–6 carry; version 1 has none. */
const alignmentCentre = (version: number): number | null =>
  version === 1 ? null : version * 4 + 10;

/* ------------------------------------------------------------------ *
 * Bit assembly
 * ------------------------------------------------------------------ */

/** Mode indicator (byte) + length + payload + terminator + padding, to the version's capacity. */
const dataCodewords = (bytes: Uint8Array, spec: VersionSpec): number[] => {
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, 8); // versions 1–9 use an 8-bit character count in this mode
  for (const byte of bytes) push(byte, 8);

  const capacity = spec.data * 8;
  push(0, Math.min(4, capacity - bits.length)); // terminator
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | (bits[i + j] ?? 0);
    codewords.push(byte);
  }

  // The two alternating pad bytes the format prescribes, to fill the version exactly.
  for (let i = 0; codewords.length < spec.data; i += 1) codewords.push(i % 2 === 0 ? 0xec : 0x11);

  return codewords;
};

/**
 * Splits the payload into blocks, computes each block's ECC, and interleaves both — which is
 * what lets a burst of damage cost every block one codeword instead of costing one block all of
 * them.
 */
const interleave = (codewords: readonly number[], spec: VersionSpec): number[] => {
  const shortLength = Math.floor(spec.data / spec.blocks);
  const longBlocks = spec.data % spec.blocks;

  const blocks: number[][] = [];
  const eccs: number[][] = [];
  let offset = 0;

  for (let i = 0; i < spec.blocks; i += 1) {
    const length = shortLength + (i >= spec.blocks - longBlocks ? 1 : 0);
    const block = codewords.slice(offset, offset + length);
    offset += length;
    blocks.push(block);
    eccs.push(remainder(block, spec.ecPerBlock));
  }

  const out: number[] = [];
  const longest = Math.max(...blocks.map((b) => b.length));

  for (let i = 0; i < longest; i += 1) {
    for (const block of blocks) {
      const byte = block[i];
      if (byte !== undefined) out.push(byte);
    }
  }
  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (const ecc of eccs) out.push(ecc[i] ?? 0);
  }

  return out;
};

/* ------------------------------------------------------------------ *
 * The matrix
 * ------------------------------------------------------------------ */

type Grid = { size: number; dark: boolean[][]; reserved: boolean[][] };

const blank = (size: number): Grid => ({
  size,
  dark: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
});

const set = (grid: Grid, row: number, col: number, dark: boolean): void => {
  if (row < 0 || col < 0 || row >= grid.size || col >= grid.size) return;
  (grid.dark[row] ?? [])[col] = dark;
  (grid.reserved[row] ?? [])[col] = true;
};

const isDark = (grid: Grid, row: number, col: number): boolean => grid.dark[row]?.[col] ?? false;
const isReserved = (grid: Grid, row: number, col: number): boolean =>
  grid.reserved[row]?.[col] ?? false;

/**
 * A finder pattern and its separator, given the centre: the 7×7 eye a scanner locks onto, ringed
 * by the light band that isolates it from the data. Both light rings — the one inside the eye and
 * the separator around it — fall out of the same Chebyshev distance.
 */
const drawFinder = (grid: Grid, row: number, col: number): void => {
  for (let dr = -4; dr <= 4; dr += 1) {
    for (let dc = -4; dc <= 4; dc += 1) {
      const distance = Math.max(Math.abs(dr), Math.abs(dc));
      set(grid, row + dr, col + dc, distance !== 2 && distance !== 4);
    }
  }
};

const drawAlignment = (grid: Grid, centre: number): void => {
  for (let dr = -2; dr <= 2; dr += 1) {
    for (let dc = -2; dc <= 2; dc += 1) {
      set(grid, centre + dr, centre + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
    }
  }
};

const drawFunctionPatterns = (grid: Grid, version: number): void => {
  const { size } = grid;

  drawFinder(grid, 3, 3);
  drawFinder(grid, 3, size - 4);
  drawFinder(grid, size - 4, 3);

  // Timing: the alternating ruler that tells a scanner where the module grid falls.
  for (let i = 8; i < size - 8; i += 1) {
    set(grid, 6, i, i % 2 === 0);
    set(grid, i, 6, i % 2 === 0);
  }

  const centre = alignmentCentre(version);
  if (centre !== null) drawAlignment(grid, centre);

  // Reserve the format-information strips; the real bits land once a mask is chosen. Index 6 is
  // skipped in both: that is where the timing ruler crosses each strip, and those two modules
  // belong to the ruler, not to the format.
  for (let i = 0; i < 9; i += 1) {
    if (i === 6) continue;
    set(grid, 8, i, false);
    set(grid, i, 8, false);
  }
  for (let i = 0; i < 8; i += 1) {
    set(grid, 8, size - 1 - i, false);
    set(grid, size - 1 - i, 8, false);
  }

  set(grid, size - 8, 8, true); // the dark module, always set
};

/** Zig-zag placement: two columns at a time, bottom to top then top to bottom, skipping col 6. */
const drawCodewords = (grid: Grid, codewords: readonly number[]): void => {
  const { size } = grid;
  let bit = 0;
  const total = codewords.length * 8;

  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing ruler: the pair shifts left past it for good, rather than
    // this one pair stepping over it — every pair after it is offset by one.
    if (right === 6) right = 5;

    for (let vertical = 0; vertical < size; vertical += 1) {
      for (let j = 0; j < 2; j += 1) {
        const col = right - j;
        const upward = ((right + 1) & 2) === 0;
        const row = upward ? size - 1 - vertical : vertical;

        if (isReserved(grid, row, col) || bit >= total) continue;

        const byte = codewords[bit >>> 3] ?? 0;
        (grid.dark[row] ?? [])[col] = ((byte >>> (7 - (bit & 7))) & 1) === 1;
        bit += 1;
      }
    }
  }
};

const MASKS: ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

const applyMask = (grid: Grid, mask: number): void => {
  const fn = MASKS[mask];
  if (fn === undefined) return;

  for (let row = 0; row < grid.size; row += 1) {
    for (let col = 0; col < grid.size; col += 1) {
      if (isReserved(grid, row, col)) continue;
      if (fn(row, col)) (grid.dark[row] ?? [])[col] = !isDark(grid, row, col);
    }
  }
};

const drawFormat = (grid: Grid, mask: number): void => {
  // 5 data bits (level M is 0b00) extended by a BCH(15,5) remainder, then masked by 0x5412 so
  // an all-zero format never reads as a valid one.
  const data = mask; // (0b00 << 3) | mask
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = (((data << 10) | rem) ^ 0x5412) & 0x7fff;
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1;

  const { size } = grid;

  for (let i = 0; i <= 5; i += 1) set(grid, i, 8, bit(i));
  set(grid, 7, 8, bit(6));
  set(grid, 8, 8, bit(7));
  set(grid, 8, 7, bit(8));
  for (let i = 9; i < 15; i += 1) set(grid, 8, 14 - i, bit(i));

  for (let i = 0; i < 8; i += 1) set(grid, 8, size - 1 - i, bit(i));
  for (let i = 8; i < 15; i += 1) set(grid, size - 15 + i, 8, bit(i));

  set(grid, size - 8, 8, true);
};

/* ------------------------------------------------------------------ *
 * Mask selection
 * ------------------------------------------------------------------ */

/**
 * The two finder-lookalikes rule 3 punishes: the 1:1:3:1:1 dark/light run of a finder pattern
 * with the four-module light band on one side or the other. They are literal module sequences,
 * not a ratio — a scaled-up 2:2:6:2:2 is not this pattern and is not penalised.
 */
const LOOKALIKE_AFTER = [true, false, true, true, true, false, true, false, false, false, false];
const LOOKALIKE_BEFORE = [false, false, false, false, true, false, true, true, true, false, true];

const lookalikes = (at: (index: number) => boolean, size: number): number => {
  let found = 0;

  for (let i = 0; i + 11 <= size; i += 1) {
    let after = true;
    let before = true;

    for (let k = 0; k < 11; k += 1) {
      const module = at(i + k);
      if (module !== LOOKALIKE_AFTER[k]) after = false;
      if (module !== LOOKALIKE_BEFORE[k]) before = false;
      if (!after && !before) break;
    }

    if (after || before) found += 1;
  }

  return found;
};

/**
 * The format's four penalty rules. Lower is better; the mask with the lowest total wins, which
 * is what keeps a code free of large same-colour fields and of shapes a scanner could mistake
 * for a finder pattern.
 */
const penalty = (grid: Grid): number => {
  const { size } = grid;
  let score = 0;
  let dark = 0;

  const line = (at: (index: number) => boolean): void => {
    // Rule 1: every run of five or more costs 3, plus one per module beyond five.
    let runLength = 1;
    let runDark = at(0);

    for (let i = 1; i < size; i += 1) {
      const module = at(i);
      if (module === runDark) {
        runLength += 1;
        continue;
      }
      if (runLength >= 5) score += runLength - 2;
      runDark = module;
      runLength = 1;
    }
    if (runLength >= 5) score += runLength - 2;

    // Rule 3: each finder-lookalike costs 40.
    score += lookalikes(at, size) * 40;
  };

  for (let row = 0; row < size; row += 1) line((col) => isDark(grid, row, col));
  for (let col = 0; col < size; col += 1) line((row) => isDark(grid, row, col));

  // Rule 2: every 2×2 block of one colour costs 3.
  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const first = isDark(grid, row, col);
      if (
        first === isDark(grid, row, col + 1) &&
        first === isDark(grid, row + 1, col) &&
        first === isDark(grid, row + 1, col + 1)
      ) {
        score += 3;
      }
    }
  }

  // Rule 4: 10 for every 5% the dark share strays from half.
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) if (isDark(grid, row, col)) dark += 1;
  }
  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return score;
};

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

export class QrTooLongError extends Error {
  constructor(bytes: number) {
    super(`${String(bytes)} bytes is more than a version-6 QR at level M can carry`);
    this.name = "QrTooLongError";
  }
}

const smallestVersion = (byteLength: number): number => {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    // Two codewords go to the mode indicator and the character count.
    const spec = VERSIONS[version];
    if (spec !== undefined && byteLength + 2 <= spec.data) return version;
  }
  throw new QrTooLongError(byteLength);
};

/**
 * Encodes `text` as the smallest level-M QR that holds it.
 *
 * Throws `QrTooLongError` only for input past version 6 — the caller decides what to draw
 * instead, and on this card that is the plain URL it would have sat beside anyway.
 */
export const encodeQr = (text: string): QrMatrix => {
  const bytes = new TextEncoder().encode(text);
  const version = smallestVersion(bytes.length);
  const spec = VERSIONS[version];
  if (spec === undefined) throw new QrTooLongError(bytes.length);

  const codewords = interleave(dataCodewords(bytes, spec), spec);

  let best: { grid: Grid; score: number } | null = null;

  for (let mask = 0; mask < MASKS.length; mask += 1) {
    const grid = blank(sizeOf(version));
    drawFunctionPatterns(grid, version);
    drawCodewords(grid, codewords);
    applyMask(grid, mask);
    drawFormat(grid, mask);

    const score = penalty(grid);
    if (best === null || score < best.score) best = { grid, score };
  }

  // `best` is set: the mask list is never empty.
  return (best?.grid ?? blank(sizeOf(version))).dark;
};
